import React from 'react';
import { useStore } from 'zustand';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';

import {
  InventoryTabItemName,
  inventoryStore,
} from '../../../../../InventoryStore';
import { useInventoryState } from '../../../../../hooks';
import { useReadOnlyRowConfig } from '../../../../../hooks/useReadOnlyRowConfig';
import { isDateAfterEndOfSale, isDatePast } from '../../../../../utils';
import type { PackDetails } from '../../../../../utils/packs';
import { PackType } from '../../../../../utils/packs';
import { PackType as TabPackType } from '../../../../Header/ActionsBar/InventoryToggle/InventoryToggle';
import { LabelCell } from '../../../../Table/Cells/LabelCell/LabelCell';
import { LabelValueCell } from '../../../../Table/Cells/LabelValueCell/LabelValueCell';
import { PureFieldCell } from '../../../../Table/Cells/PureFieldCell/PureFieldCell';
import { TableRow } from '../../../../Table/TableRow/TableRow';
import { formatTooltip } from '../utils';

const PackLowestPriceRowComponent: React.FC<{ packId: number }> = ({
  packId,
}) => {
  const { roomsList, experiencesList, originalsList } =
    React.useContext(ModernHotelContext);
  const { data: experiencesCategories } = useExperiencesCategories();
  const { currencyFormatter } = React.useContext(CurrencyContext);

  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);

  const { inventoryPacks, dateKeys } = useReadOnlyRowConfig(
    InventoryTabItemName.Packs
  );

  const { endOfSaleDate, packType } = useInventoryState();

  if (!inventoryPacks) {
    return null;
  }

  return (
    <TableRow>
      <LabelCell
        label={
          packType === TabPackType.NIGHT_STAY ? 'Lowest price' : 'Package price'
        }
      />
      {dateKeys.map((dateKey) => {
        const fieldName = [dateKey, `${packId}`];
        const bookedStockFieldName = [...fieldName, 'priceFrom'];
        const isGrayed =
          isDatePast(dateKey) || isDateAfterEndOfSale(dateKey, endOfSaleDate);

        return (
          <PureFieldCell key={dateKey} name={fieldName} dateKey={dateKey}>
            {(values?: PackDetails) => {
              if (!values || values.type !== PackType.WITH_ROOM) {
                return null;
              }

              return (
                <LabelValueCell
                  value={values?.priceFrom}
                  date={dateKey}
                  label={
                    values?.priceFromDiscount !== undefined
                      ? `-${values.priceFromDiscount}%`
                      : undefined
                  }
                  labelPosition="bottom"
                  name={bookedStockFieldName}
                  grayed={isGrayed}
                  formatter={(v) =>
                    v !== undefined
                      ? currencyFormatter(!isEditMode ? Math.floor(v) : v)
                      : '-'
                  }
                  tooltip={formatTooltip(
                    values,
                    currencyFormatter,
                    roomsList,
                    [...experiencesList, ...originalsList],
                    experiencesCategories
                  )}
                />
              );
            }}
          </PureFieldCell>
        );
      })}
    </TableRow>
  );
};

export const PackLowestPriceRow = React.memo(PackLowestPriceRowComponent);
